import { selectCurrentIDToken } from '../store/slices/auth/authSelectors';
import { selectPermitApplicationData } from '../store/slices/apply/applySelectors';
import { selectConfigExpressApplyRentalStructureRequired } from '../store/slices/config/configSelectors';
import { getAPIManagerInstance } from './apiManager';
import { DECKARD_API_URL, httpMethodFunctions } from './common';
import { RENTAL_STRUCTURE_STATUS_ENUM } from '../common/enums';
import { sanitizePeople } from './utils';
import { RootState } from 'src/store/store';
import { CardConnectApiData, CyberSourceApiData } from 'src/types/payment';

export const getStripePublicKey = async () => {
  const { data } = await httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/stripe-public`
  );
  return data;
};

export const payGeneric = async (
  paymentId: string,
  signatory: string,
  email: string
) => {
  const { data } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payGeneric`,
    {
      paymentId,
      signatory,
      email,
    }
  );
  return data;
};

export const payByCreditCard = async (
  paymentId: string,
  signatory: string,
  email: string
) => {
  const { data } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payCC`,
    {
      paymentId,
      signatory,
      email,
    }
  );
  return data;
};

export const payByACHDirectDebit = async (
  paymentId: string,
  signatory: string,
  email: string
) => {
  const { data } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payACHDirectDebit`,
    {
      paymentId,
      signatory,
      email,
    }
  );
  return data;
};

export const payByCheck = async (
  paymentId: string,
  signatory: string,
  email: string
) => {
  const { data } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payCheck`,
    {
      paymentId,
      signatory,
      email,
    }
  );
  return data;
};

export const calculateMultiPropertyTotAsync = async (
  state: RootState,
  body: unknown
) => {
  const accessToken = selectCurrentIDToken(state);
  return await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/calculateMultiPropertyTot`,
    {
      ...(body as any),
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const payNothingGeneric = async (
  state: RootState,
  paymentId: string,
  signatory: string,
  email: string
) => {
  const accessToken = selectCurrentIDToken(state);

  const payNothingConfig = {
    signatory,
    paymentId,
    email,
  };

  const { data, status } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payNothing`,
    {
      ...payNothingConfig,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  if (status === 200) {
    return data;
  } else {
    console.error('Failed on payNothing endpoint', data);
    throw new Error('Failed on payNothing endpoint');
  }
};

export const checkTaxPayment = async (state: RootState, paymentID: string) => {
  const accessToken = selectCurrentIDToken(state);
  const { data } = await httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/checkTotPayment/${paymentID}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return data;
};

export const checkLicensePayment = async (
  state: RootState,
  paymentID: string
) => {
  const accessToken = selectCurrentIDToken(state);
  const { data } = await httpMethodFunctions.get<{
    id: string;
    paymentConfirmation: string;
  }>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/checkRenewalPayment/${paymentID}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return data;
};

export const payNothing = async (
  state: RootState,
  paymentID?: string | number,
  email?: string
) => {
  const { registrant, property, contacts, propertyDetails, certifications } =
    selectPermitApplicationData(state) ?? {};
  const rentalStructureRequiredStatus =
    selectConfigExpressApplyRentalStructureRequired(state);

  const people: any = {
    ...sanitizePeople(registrant),
    ...sanitizePeople(contacts),
  };

  const payNothingConfig = {
    registrantType: registrant?.registrantType ?? '',
    unitNumber: property?.unitnumber,
    // only add rentalStructure property if it is not set to hidden in the config
    ...(rentalStructureRequiredStatus !==
      RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
      rentalStructure: property?.rentalstructure ?? null,
    }),
    misc: {
      supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
      mandatoryAcknowledgements: {
        ...certifications?.mandatoryAcknowledgements,
      },
    },
    people: { ...people, owner: [people.owner], registrantType: undefined },
    signatory: certifications?.signatory,
    paymentId: paymentID,
    email: email,
  };

  const { data } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payNothing`,
    {
      ...payNothingConfig,
    }
  );
  return data;
};

export const initiateCybersourcePayment = async (data: CyberSourceApiData) => {
  return await httpMethodFunctions.post<CyberSourceApiData>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/cyberSourceInitiatePayment`,
    data
  );
};

export const cardConnectPostback = async (data: CardConnectApiData) => {
  return await httpMethodFunctions.post<CardConnectApiData>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/cardConnectPostback`,
    data
  );
};

export const downloadTaxSummary = async (
  paymentId: string,
  accessToken: string
): Promise<Blob> => {
  const { data } = await httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/downloadTaxPaymentSummary/${paymentId}`,
    {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return data as Blob;
};
